import { Component } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Course, CourseSubscriptionSession, CourseSubscriptionSessionProvider, StatisticsProvider } from '@stuplay';
import { SectionContentSession } from '@stuplay';
import { BackUrlService } from '../../utils/services/back-url.service';
import { TimerService } from '../../utils/components/timer/timer.service';
import { UserProvider } from '../../utils/services/user.provider';
import { StorageService } from '../../utils/services/storage.service';

@Component({
    selector: 'app-results',
    templateUrl: './results.component.html',
})
export class ResultsComponent {
    public course: Course | any;
    public courseSubscriptionSession: CourseSubscriptionSession;
    public lang: string = 'fr';
    // Datos GENERALES desde reporting (autoritativos). null = aún no cargados.
    public reportProgress: number = null; // 0-100
    public reportScore: number = null;    // 0-100
    // Tiempo TOTAL (en segundos) que el alumno ha estado activo en el curso, sumando todas sus
    // sesiones de contenido. Viene del campo context.duration del servicio de reporting (autoritativo).
    public reportDuration: number = null; // segundos

    constructor(
        private route: ActivatedRoute,
        private backUrlService: BackUrlService,
        public timerService: TimerService,
        private courseSubscriptionSessionProvider: CourseSubscriptionSessionProvider,
        private userProvider: UserProvider,
        private statisticsProvider: StatisticsProvider,
        private storageService: StorageService,
    ) {
        this.route.parent.data.subscribe(data => {
            this.lang = this.userProvider?.currentUser?.lang;
            this.course = data.course;
            this.courseSubscriptionSession = data.courseSubscriptionSession;
            this.loadReportProgress();

            const timeout = setTimeout(() => {
                this.courseSubscriptionSessionProvider.getCourseSubscriptionSession(this.courseSubscriptionSession?.id).subscribe((data) => {
                    this.courseSubscriptionSession = data;
                    clearTimeout(timeout);
                });
            }, 500);
        });
    }

    /**
     * Carga el progreso/score/duración GENERAL desde el servicio de reporting (fuente autoritativa).
     */
    private loadReportProgress(): void {
        const companyId = this.storageService.get('company')?.id;
        const accountId = this.storageService.get('me')?.id;
        const courseId = this.course?.id;
        if (!companyId || !accountId || !courseId) { return; }

        this.statisticsProvider.getContentSubscriptionReport(companyId, courseId, accountId).subscribe((report) => {
            if (report) {
                this.reportProgress = report.progress;
                this.reportScore = report.score;
                this.reportDuration = report.duration;
            }
        });
    }

    getSectionContentSession(id: number): SectionContentSession {
        return this.courseSubscriptionSession.sectionContentSessions.find(({ sectionContentId }) => sectionContentId === id);
    }

    getTotalLessons(): number {
        if (!this.course?.sections) { return 0; }
        return this.course.sections.reduce((acc: number, s: any) => acc + (s?.sectionContents?.length || 0), 0);
    }

    getCompletedLessons(): number {
        if (!this.courseSubscriptionSession?.sectionContentSessions) { return 0; }
        return this.courseSubscriptionSession.sectionContentSessions.filter((s: any) => !!s?.doneAt).length;
    }

    getOverallProgress(): number {
        // Si todas las secciones están completadas localmente, forzar 100%
        if (this.course?.sections?.length > 0) {
            const allDone = this.course.sections.every((s: any) =>
                this.courseSubscriptionSession?.getSectionProgress(s) >= 1
            );
            if (allDone) { return 1; }
        }
        // Progreso GENERAL autoritativo desde reporting (si ya cargó). Normalizado a 0-1.
        if (this.reportProgress != null) {
            return this.reportProgress > 1 ? this.reportProgress / 100 : this.reportProgress;
        }
        const raw = this.courseSubscriptionSession?.progress;
        if (raw == null) { return 0; }
        return raw > 1 ? raw / 100 : raw;
    }

    /**
     * Score/nota GENERAL (0-100). Usa el valor de reporting si está disponible.
     * Devuelve null si no hay score (para mostrar el estado "sin evaluaciones").
     */
    getScore(): number {
        if (this.reportScore != null) { return this.reportScore; }
        return this.courseSubscriptionSession?.getCampScore();
    }

    /**
     * Tiempo TOTAL de dedicación al curso, en SEGUNDOS.
     *
     * ¿Qué tiempo es? Es el tiempo acumulado que el alumno ha estado activo dentro del curso
     * (suma del tiempo de todas las sesiones de contenido), medido por el cronómetro del player
     * (TimerService) y consolidado por la plataforma. NO es la duración del curso ni un tiempo estimado.
     *
     * ¿En base a qué se calcula? Al valor autoritativo context.duration del servicio de reporting.
     * Si aún no cargó, cae al acumulado local courseSubscriptionSession.getDuration().
     *
     * El template lo formatea con el pipe `time:true`, que interpreta el número como SEGUNDOS y lo
     * redondea: >=3600s → "N horas" (Math.round(seg/3600)). Por eso ~5400–9000s se muestra como "2 horas".
     */
    getDurationValue(): number {
        // 1) Si ya llegó el dato de reporting, ese manda (tiempo total real en segundos).
        if (this.reportDuration != null) { return this.reportDuration; }
        // 2) Mientras carga reporting, usar el acumulado local de la sesión (en segundos).
        return this.courseSubscriptionSession?.getDuration ? this.courseSubscriptionSession.getDuration() : 0;
    }

    get backUrl(): string {
        return this.backUrlService.backUrl;
    }

    back(): void {
        parent.postMessage('closeIframePlayer', '*');
        document.location.replace(this.backUrlService.getBackUrl());
    }
}
